<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ContactAPI from 'dashboard/api/contacts';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { chatPathInList } from 'dashboard/helper/conversationListPath';
import { useS3Kanban } from 'dashboard/composables/useS3Kanban';

// Smith3D print-job kanban (JR admin system) shown where the "select a
// conversation" placeholder sits, so the chat list stays beside it. The page
// hands a chat back with
//   window.parent.postMessage({ event: 'openChat', conversationId: 123 }, '*')
// or { event: 'openChat', phone: '0123456789' } (their WhatsApp chat).
const KANBAN_ORIGIN = 'https://smith3d-admin.homestayvilla.com';
const S3_WHATSAPP_INBOX_ID = 62;

const route = useRoute();
const router = useRouter();
const frameKey = ref(0);
const { kanbanPath, kanbanRequest, showKanbanPage } = useS3Kanban();
// Each agent's starting board (e.g. Normann → /preorders) lives in their
// custom_attributes.s3_kanban_default; used until a chip or menu picks another.
const currentUser = useMapGetter('getCurrentUser');
const startPage = currentUser.value?.custom_attributes?.s3_kanban_default;
if (
  kanbanRequest.value === 0 &&
  typeof startPage === 'string' &&
  startPage.startsWith('/')
) {
  kanbanPath.value = startPage;
}
// Sidebar Kanban menu links carry ?kanban=/requests etc.
watch(
  () => route.query.kanban,
  page => {
    if (typeof page === 'string' && page.startsWith('/')) showKanbanPage(page);
  },
  { immediate: true }
);
const kanbanUrl = computed(() => `${KANBAN_ORIGIN}${kanbanPath.value}`);

const openConversation = conversationId =>
  router.push(chatPathInList(route.path, conversationId));

const openByPhone = async phone => {
  let digits = String(phone).replace(/\D/g, '');
  if (digits.startsWith('0')) digits = `6${digits}`;
  const { data } = await ContactAPI.search(digits);
  const contact = (data.payload || []).find(
    c =>
      String(c.phone_number || '').replace(/\D/g, '') === digits ||
      String(c.identifier || '').startsWith(`${digits}@`)
  );
  if (!contact) return;
  const { data: conversations } = await ContactAPI.getConversations(contact.id);
  const latest = (conversations.payload || [])
    .filter(c => c.inbox_id === S3_WHATSAPP_INBOX_ID)
    .sort((a, b) => b.last_activity_at - a.last_activity_at)[0];
  if (latest) {
    openConversation(latest.id);
    return;
  }
  router.push({
    name: 'contacts_edit',
    params: { accountId: route.params.accountId, contactId: contact.id },
  });
};

const onKanbanMessage = event => {
  if (event.origin !== KANBAN_ORIGIN) return;
  let data = event.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return;
    }
  }
  if (data?.event !== 'openChat') return;
  if (Number(data.conversationId) > 0) {
    openConversation(Number(data.conversationId));
  } else if (data.phone) {
    openByPhone(data.phone);
  }
};

onMounted(() => window.addEventListener('message', onKanbanMessage));
onBeforeUnmount(() => window.removeEventListener('message', onKanbanMessage));
</script>

<template>
  <div class="flex flex-col w-full h-full min-w-0">
    <div
      class="flex items-center gap-2 px-4 py-1.5 border-b border-n-weak shrink-0"
    >
      <span class="flex-1 text-sm font-medium truncate text-n-slate-12">
        {{ $t('S3_KANBAN.TITLE') }}
      </span>
      <NextButton
        v-tooltip.bottom="$t('S3_KANBAN.RELOAD')"
        icon="i-lucide-refresh-cw"
        slate
        faded
        xs
        @click="frameKey += 1"
      />
      <a :href="kanbanUrl" target="_blank" rel="noopener noreferrer">
        <NextButton
          v-tooltip.bottom="$t('S3_KANBAN.OPEN_NEW_TAB')"
          icon="i-lucide-external-link"
          slate
          faded
          xs
        />
      </a>
    </div>
    <iframe
      :key="`${frameKey}-${kanbanRequest}`"
      :src="kanbanUrl"
      class="flex-1 w-full border-0"
      :title="$t('S3_KANBAN.TITLE')"
    />
  </div>
</template>
